import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import axios from 'axios'
import Navbar from '../components/Navbar'
import { useAuth, displayUser } from '../context/AuthContext'
import { sanitizeBio } from '../utils/renderBio'
import { resizeImage } from '../lib/resizeImage'

const RESIZE_FALLBACK_MSG = "We couldn't process that image. Try a different photo, or save it as JPEG and retry."

// ── Gradient / colour presets ──────────────────────────────────────────────────

const AVATAR_GRADS = [
  'linear-gradient(135deg, #7C3AED, #EC4899)',
  'linear-gradient(135deg, #0D9488, #7C3AED)',
  'linear-gradient(135deg, #1D4ED8, #0D9488)',
  'linear-gradient(135deg, #EC4899, #F97316)',
  'linear-gradient(135deg, #BE123C, #7C3AED)',
  'linear-gradient(135deg, #059669, #1D4ED8)',
  'linear-gradient(135deg, #F97316, #EC4899)',
  'linear-gradient(135deg, #7C3AED, #1D4ED8)',
]

const BANNER_GRADS = [
  'linear-gradient(135deg, #1E0A3C 0%, #3B0764 40%, #831843 100%)',
  'linear-gradient(135deg, #0F4C75 0%, #1E0A3C 50%, #3B0764 100%)',
  'linear-gradient(135deg, #064E3B 0%, #0F4C75 50%, #1E0A3C 100%)',
  'linear-gradient(135deg, #3B0764 0%, #831843 50%, #1D4ED8 100%)',
  'linear-gradient(135deg, #1E0A3C 0%, #831843 60%, #7C3AED 100%)',
  'linear-gradient(135deg, #0F172A 0%, #1E0A3C 50%, #3B0764 100%)',
  'linear-gradient(135deg, #1D4ED8 0%, #1E0A3C 50%, #831843 100%)',
  'linear-gradient(135deg, #064E3B 0%, #1E0A3C 60%, #3B0764 100%)',
]

const BG_PRESETS = [
  { label: 'Default',       value: '' },
  { label: 'Snow',          value: '#FFFFFF' },
  { label: 'Lavender',      value: 'linear-gradient(160deg, #FAF5FF 0%, #EDE9FE 100%)' },
  { label: 'Rose',          value: 'linear-gradient(160deg, #FFF1F5 0%, #FCE7F3 100%)' },
  { label: 'Mint',          value: 'linear-gradient(160deg, #F0FDF9 0%, #CCFBF1 100%)' },
  { label: 'Twilight',      value: 'linear-gradient(160deg, #1E0A3C 0%, #3B0764 100%)' },
  { label: 'Midnight',      value: 'linear-gradient(160deg, #0F172A 0%, #1E0A3C 100%)' },
  { label: 'Deep Ocean',    value: 'linear-gradient(160deg, #0C1445 0%, #0F4C75 100%)' },
  { label: 'Cosmos',        value: 'linear-gradient(160deg, #0F172A 0%, #1E0A3C 40%, #3B0764 100%)' },
  { label: 'Forest',        value: 'linear-gradient(160deg, #052E16 0%, #064E3B 100%)' },
]

// ── Subcomponents ──────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <section className="ep-section">
      <h2 className="ep-section__title">{title}</h2>
      {children}
    </section>
  )
}

function Field({ label, hint, children }) {
  return (
    <div className="ep-field">
      <label className="ep-field__label">{label}</label>
      {hint && <span className="ep-field__hint">{hint}</span>}
      {children}
    </div>
  )
}

function GradPicker({ value, options, onChange, shape = 'circle' }) {
  return (
    <div className={`ep-grad-picker ep-grad-picker--${shape}`}>
      {options.map(g => (
        <button
          key={g}
          type="button"
          className={`ep-grad-swatch ep-grad-swatch--${shape}${value === g ? ' ep-grad-swatch--active' : ''}`}
          style={{ background: g }}
          onClick={() => onChange(g)}
          aria-label="Pick gradient"
        />
      ))}
    </div>
  )
}

function BgPicker({ value, onChange }) {
  const [customColor, setCustomColor] = useState('')

  return (
    <div className="ep-bg-picker">
      <div className="ep-bg-presets">
        {BG_PRESETS.map(p => (
          <button
            key={p.value}
            type="button"
            className={`ep-bg-swatch${value === p.value ? ' ep-bg-swatch--active' : ''}`}
            style={{ background: p.value || 'var(--bg)' }}
            onClick={() => onChange(p.value)}
            title={p.label}
          >
            {!p.value && <span className="ep-bg-swatch__default">⬜</span>}
          </button>
        ))}
      </div>
      <div className="ep-bg-custom">
        <span className="ep-field__label" style={{ fontSize: '0.78rem' }}>Custom colour</span>
        <div className="ep-bg-custom__row">
          <input
            type="color"
            className="ep-color-input"
            value={customColor || '#FAF5FF'}
            onChange={e => { setCustomColor(e.target.value); onChange(e.target.value) }}
          />
          <input
            type="text"
            className="ep-input ep-input--sm"
            placeholder="#FAF5FF or css gradient…"
            value={!BG_PRESETS.find(p => p.value === value) ? (value || '') : ''}
            onChange={e => onChange(e.target.value)}
          />
        </div>
      </div>
    </div>
  )
}

function FandomInput({ fandoms, onChange }) {
  const [input, setInput] = useState('')

  function add() {
    const v = input.trim()
    if (!v || fandoms.includes(v) || fandoms.length >= 20) return
    onChange([...fandoms, v])
    setInput('')
  }

  function remove(f) {
    onChange(fandoms.filter(x => x !== f))
  }

  return (
    <div className="ep-tag-input">
      <div className="ep-tag-chips">
        {fandoms.map(f => (
          <span key={f} className="ep-tag-chip">
            {f}
            <button type="button" className="ep-tag-chip__remove" onClick={() => remove(f)} aria-label={`Remove ${f}`}>×</button>
          </span>
        ))}
        {fandoms.length === 0 && <span className="ep-tag-chips__empty">No fandoms added yet</span>}
      </div>
      <div className="ep-tag-input__row">
        <input
          type="text"
          className="ep-input"
          placeholder="Add a fandom…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          maxLength={60}
        />
        <button type="button" className="ep-tag-add-btn" onClick={add} disabled={!input.trim()}>Add</button>
      </div>
    </div>
  )
}

function BioEditor({ value, onChange }) {
  const editorRef = useRef(null)
  const hasEdited = useRef(false)
  const [fmt, setFmt] = useState({ bold: false, italic: false, underline: false })

  // Keep editor in sync with value prop until the user starts editing
  useEffect(() => {
    const el = editorRef.current
    if (!el || hasEdited.current) return
    el.innerHTML = sanitizeBio(value || '')
  }, [value])

  function updateFmt() {
    const sel = window.getSelection()
    const node = sel?.anchorNode
    let inBlockquote = false
    if (node) {
      let el = node.nodeType === 3 ? node.parentElement : node
      while (el && el !== editorRef.current) {
        if (el.tagName === 'BLOCKQUOTE') { inBlockquote = true; break }
        el = el.parentElement
      }
    }
    setFmt({
      bold:       document.queryCommandState('bold'),
      italic:     document.queryCommandState('italic'),
      underline:  document.queryCommandState('underline'),
      blockquote: inBlockquote,
    })
  }

  function execCmd(cmd, arg) {
    editorRef.current?.focus()
    document.execCommand('styleWithCSS', false, true)
    document.execCommand(cmd, false, arg ?? null)
    onChange(editorRef.current?.innerHTML ?? '')
    updateFmt()
  }

  function handleLink(e) {
    e.preventDefault()
    const sel = window.getSelection()
    const range = sel?.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null
    const url = window.prompt('Enter URL:')
    if (!url) return
    if (!/^https?:\/\//i.test(url)) {
      window.alert('Only http:// and https:// links are allowed.')
      return
    }
    editorRef.current?.focus()
    if (range) { sel.removeAllRanges(); sel.addRange(range) }
    document.execCommand('createLink', false, url)
    onChange(editorRef.current?.innerHTML ?? '')
  }

  function handleInput() {
    hasEdited.current = true
    onChange(editorRef.current?.innerHTML ?? '')
  }

  function handlePaste(e) {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  const textLen = value.replace(/<[^>]*>/g, '').length

  return (
    <div className="ep-bio-editor">
      <div className="ep-bio-toolbar">
        <button type="button" className={`ep-bio-btn${fmt.bold      ? ' ep-bio-btn--active' : ''}`} onMouseDown={e => { e.preventDefault(); execCmd('bold') }}          title="Bold"><strong>B</strong></button>
        <button type="button" className={`ep-bio-btn${fmt.italic    ? ' ep-bio-btn--active' : ''}`} onMouseDown={e => { e.preventDefault(); execCmd('italic') }}        title="Italic"><em>I</em></button>
        <button type="button" className={`ep-bio-btn${fmt.underline ? ' ep-bio-btn--active' : ''}`} onMouseDown={e => { e.preventDefault(); execCmd('underline') }}     title="Underline"><u>U</u></button>
        <button type="button" className="ep-bio-btn" onMouseDown={handleLink}                                                                                            title="Link">🔗</button>
        <button type="button" className={`ep-bio-btn${fmt.blockquote ? ' ep-bio-btn--active' : ''}`} onMouseDown={e => { e.preventDefault(); execCmd('formatBlock', fmt.blockquote ? 'P' : 'BLOCKQUOTE') }} title="Quote">"</button>
        <button type="button" className="ep-bio-btn" onMouseDown={e => { e.preventDefault(); execCmd('insertUnorderedList') }}                                          title="List">•</button>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyUp={updateFmt}
        onMouseUp={updateFmt}
        onSelect={updateFmt}
        className="ep-bio-textarea ep-bio-richtext"
        data-placeholder="Tell readers about yourself…"
      />

      <div className="ep-bio-footer">
        <span className="ep-char-count">{textLen}/5000</span>
      </div>
    </div>
  )
}

// ── Image upload ───────────────────────────────────────────────────────────────

function ImageUpload({ type, currentUrl, onUploaded, getToken }) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError]         = useState(null)

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return }
    setError(null)
    setUploading(true)
    try {
      let upload
      try {
        const maxEdge = type === 'avatar' ? 512 : 1920
        const blob = await resizeImage(file, { maxEdge })
        upload = blob instanceof File ? blob : new File([blob], file.name, { type: blob.type })
      } catch {
        setError(RESIZE_FALLBACK_MSG)
        setUploading(false)
        e.target.value = ''
        return
      }
      const token    = await getToken()
      const formData = new FormData()
      formData.append('file', upload)
      const { data } = await axios.post(`/api/profile/upload/${type}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      })
      onUploaded(data.url)
    } catch (err) {
      // Sentry reported {code, message} objects leaking into this state —
      // guard against an upstream error field that's an object (Supabase
      // error shape) rather than a string. Falls through to err.message
      // before the literal fallback so network errors still surface useful text.
      const serverErr = err?.response?.data?.error
      setError((typeof serverErr === 'string' ? serverErr : null) ?? err?.message ?? 'Upload failed')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  return (
    <div className="ep-img-upload">
      {currentUrl && (
        <div className={`ep-img-upload__preview ep-img-upload__preview--${type}`}>
          <img src={currentUrl} alt={type} />
        </div>
      )}
      <div className="ep-img-upload__row">
        <button type="button" className="btn btn-outline ep-img-upload__btn" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? 'Uploading…' : currentUrl ? `Change ${type}` : `Upload ${type}`}
        </button>
        {currentUrl && (
          <button type="button" className="ep-img-upload__remove" onClick={() => onUploaded(null)}>Remove</button>
        )}
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      </div>
      {error && <span className="ep-img-upload__error">{error}</span>}
    </div>
  )
}

// ── Preview card ───────────────────────────────────────────────────────────────

function ProfilePreview({ form }) {
  const initial = (form.displayName || '?')[0].toUpperCase()
  return (
    <div className="ep-preview">
      <div className="ep-preview__label">Preview</div>
      <div className="ep-preview__card">
        <div className="ep-preview__banner" style={{ background: form.bannerUrl ? undefined : form.bannerGrad }}>
          {form.bannerUrl
            ? <img src={form.bannerUrl} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <><div className="ep-preview__banner-blob ep-preview__banner-blob--1" /><div className="ep-preview__banner-blob ep-preview__banner-blob--2" /></>
          }
        </div>
        <div className="ep-preview__body">
          <div className="ep-preview__avatar" style={{ background: form.avatarUrl ? undefined : form.avatarGrad }}>
            {form.avatarUrl
              ? <img src={form.avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : initial
            }
          </div>
          <div className="ep-preview__info">
            <div className="ep-preview__name">{form.displayName || 'Display Name'}</div>
            {form.tagline && <div className="ep-preview__tagline">{form.tagline}</div>}
            {form.pronouns && <span className="ep-preview__pronouns">{form.pronouns}</span>}
          </div>
          {form.bio && (
            <div
              className="ep-preview__bio"
              dangerouslySetInnerHTML={{ __html: sanitizeBio(form.bio) }}
            />
          )}
        </div>
        {form.profileBg && (
          <div className="ep-preview__bg-note">
            <span>Background:</span>
            <div className="ep-preview__bg-swatch" style={{ background: form.profileBg }} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

const DEFAULT_FORM = {
  displayName: '',
  tagline:     '',
  pronouns:    '',
  bio:         '',
  location:    '',
  website:     '',
  avatarGrad:  AVATAR_GRADS[0],
  bannerGrad:  BANNER_GRADS[0],
  avatarUrl:   null,
  bannerUrl:   null,
  profileBg:   '',
  fandoms:     [],
  notificationPrefs: {},
  pinnedStoryId: null,
  showOnlineStatus: true,
  showWall: true,
  privacyMode: false,
  followRequestCooldownAfterReject: 'permanent',
}

export default function EditProfilePage() {
  const { user, profile, getToken } = useAuth()
  const currentUser = displayUser(user, profile)
  const navigate    = useNavigate()
  // Teen child-safety: external-link field is a contact-discovery vector.
  // Server also strips the field from responses and rejects PATCH of it.
  const isTeen = profile?.accountType === 'teen'

  const [form, setForm]     = useState(DEFAULT_FORM)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving]   = useState(false)
  const [saved, setSaved]     = useState(false)
  const [error, setError]     = useState(null)
  const [publishedStories, setPublishedStories] = useState([])

  // Load existing profile
  useEffect(() => {
    async function load() {
      try {
        const token = await getToken()
        const { data } = await axios.get('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
        setForm({
          displayName: data.displayName ?? '',
          tagline:     data.tagline     ?? '',
          pronouns:    data.pronouns    ?? '',
          bio:         data.bio         ?? '',
          location:    data.location    ?? '',
          website:     data.website     ?? '',
          avatarGrad:  data.avatarGrad  ?? AVATAR_GRADS[0],
          bannerGrad:  data.bannerGrad  ?? BANNER_GRADS[0],
          avatarUrl:   data.avatarUrl   ?? null,
          bannerUrl:   data.bannerUrl   ?? null,
          profileBg:   data.profileBg   ?? '',
          fandoms:     data.fandoms     ?? [],
          notificationPrefs: data.notificationPrefs ?? {},
          pinnedStoryId: data.pinnedStoryId ?? null,
          showOnlineStatus: data.showOnlineStatus ?? true,
          showWall: data.showWall ?? true,
          privacyMode: data.privacyMode ?? false,
          followRequestCooldownAfterReject: data.followRequestCooldownAfterReject ?? 'permanent',
        })
      } catch {
        // Use defaults — profile may not exist yet
        if (currentUser) {
          setForm(f => ({ ...f, displayName: currentUser.displayName ?? '' }))
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [getToken, currentUser?.username])

  // Fetch published stories for featured story dropdown
  useEffect(() => {
    if (!currentUser?.username) return
    axios.get(`/api/profile/${currentUser.username}`)
      .then(r => setPublishedStories((r.data.stories ?? []).filter(s => s.published !== false)))
      .catch(() => {})
  }, [currentUser?.username])

  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }))
    setSaved(false)
  }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const token = await getToken()
      await axios.patch('/api/profile', {
        display_name: form.displayName.trim(),
        tagline:      form.tagline.trim()   || null,
        pronouns:     form.pronouns.trim()  || null,
        bio:          form.bio.trim()       || null,
        location:     form.location.trim()  || null,
        website:      form.website.trim()   || null,
        avatar_grad:  form.avatarGrad,
        banner_grad:  form.bannerGrad,
        avatar_url:   form.avatarUrl        ?? null,
        banner_url:   form.bannerUrl        ?? null,
        profile_bg:   form.profileBg        || null,
        fandoms:      form.fandoms,
        notification_prefs: form.notificationPrefs,
        pinned_story_id: form.pinnedStoryId,
        show_online_status: form.showOnlineStatus,
        show_wall: form.showWall,
        privacy_mode: form.privacyMode ?? false,
        follow_request_cooldown_after_reject: form.followRequestCooldownAfterReject ?? 'permanent',
      }, { headers: { Authorization: `Bearer ${token}` } })

      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      // Defensive: typeof check on server error — see ImageUpload catch above.
      const serverErr = err?.response?.data?.error
      setError((typeof serverErr === 'string' ? serverErr : null) ?? err?.message ?? 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="ep-loading">Loading your profile…</div>
      </>
    )
  }

  return (
    <div className="ep-page">
      <Navbar />

      <div className="ep-header-bar">
        <div className="ep-header-bar__inner">
          <div>
            <h1 className="ep-header-bar__title">Edit Profile</h1>
            <p className="ep-header-bar__sub">Changes are visible to everyone who visits your profile.</p>
          </div>
          <div className="ep-header-bar__actions">
            {currentUser && (
              <Link to={`/user/${currentUser.username}`} className="btn btn-outline ep-header-btn">
                View profile
              </Link>
            )}
            <button
              className="btn btn-primary ep-header-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>

      {error && <div className="ep-error-banner">{error}</div>}

      <div className="ep-body">
        <form className="ep-form" onSubmit={handleSave}>

          {/* ── Appearance ── */}
          <Section title="Appearance">
            <Field label="Profile picture" hint="Upload an image, or pick a gradient below.">
              <ImageUpload type="avatar" currentUrl={form.avatarUrl} onUploaded={url => set('avatarUrl', url)} getToken={getToken} />
              <GradPicker value={form.avatarGrad} options={AVATAR_GRADS} onChange={v => set('avatarGrad', v)} shape="circle" />
            </Field>
            <Field label="Banner" hint="Upload an image, or pick a gradient below.">
              <ImageUpload type="banner" currentUrl={form.bannerUrl} onUploaded={url => set('bannerUrl', url)} getToken={getToken} />
              <GradPicker value={form.bannerGrad} options={BANNER_GRADS} onChange={v => set('bannerGrad', v)} shape="strip" />
            </Field>
            <Field label="Profile background" hint="Sets the background of your whole profile page.">
              <BgPicker value={form.profileBg} onChange={v => set('profileBg', v)} />
            </Field>
          </Section>

          {/* ── Identity ── */}
          <Section title="Identity">
            <Field label="Display name">
              <input
                type="text"
                className="ep-input"
                value={form.displayName}
                onChange={e => set('displayName', e.target.value)}
                maxLength={60}
                placeholder="How your name appears on your profile"
              />
            </Field>
            <Field label="Tagline" hint="A short line that appears under your name — 120 characters max.">
              <div className="ep-input-wrap">
                <input
                  type="text"
                  className="ep-input"
                  value={form.tagline}
                  onChange={e => set('tagline', e.target.value)}
                  maxLength={120}
                  placeholder="e.g. fantasy writer · updates sundays"
                />
                <span className="ep-char-count ep-char-count--inline">{form.tagline.length}/120</span>
              </div>
            </Field>
            <Field label="Pronouns">
              <input
                type="text"
                className="ep-input ep-input--sm"
                value={form.pronouns}
                onChange={e => set('pronouns', e.target.value)}
                maxLength={30}
                placeholder="she/her, he/him, they/them…"
              />
            </Field>
            <Field label="Location">
              <input
                type="text"
                className="ep-input ep-input--sm"
                value={form.location}
                onChange={e => set('location', e.target.value)}
                maxLength={100}
                placeholder="City, Country"
              />
            </Field>
            {!isTeen && (
              <Field label="Website">
                <input
                  type="url"
                  className="ep-input ep-input--sm"
                  value={form.website}
                  onChange={e => set('website', e.target.value)}
                  placeholder="https://your-site.com"
                />
              </Field>
            )}
          </Section>

          {/* ── Bio ── */}
          <Section title="Bio">
            <BioEditor value={form.bio} onChange={v => set('bio', v)} />
          </Section>

          {/* ── Featured Story ── */}
          {publishedStories.length > 0 && (
            <Section title="Featured Story">
              <p className="ep-section__desc">Pin one of your published stories to the top of your profile.</p>
              <select
                className="ep-input"
                value={form.pinnedStoryId ?? ''}
                onChange={e => set('pinnedStoryId', e.target.value || null)}
              >
                <option value="">None</option>
                {publishedStories.map(s => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </select>
            </Section>
          )}

          {/* ── Interests ── */}
          <Section title="Fandoms & Interests">
            <p className="ep-section__desc">Add the fandoms and communities you write for. These show up on your profile's About tab.</p>
            <FandomInput fandoms={form.fandoms} onChange={v => set('fandoms', v)} />
          </Section>

          {/* ── Privacy ── */}
          <Section title="Privacy">
            <Field label="Online status" hint="When enabled, other users can see when you're active on Verse.">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.showOnlineStatus ?? true}
                  onChange={(e) => set('showOnlineStatus', e.target.checked)}
                />
                <span style={{ fontSize: '0.85rem' }}>Show when I'm online</span>
              </label>
            </Field>
            <Field label="Wall visibility" hint="When disabled, your wall tab is hidden from other users.">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.showWall ?? true}
                  onChange={(e) => set('showWall', e.target.checked)}
                />
                <span style={{ fontSize: '0.85rem' }}>Show wall on profile</span>
              </label>
            </Field>
            <Field label="Approval to follow" hint="When enabled, new followers send a request you can accept or decline. Existing followers stay.">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.privacyMode ?? false}
                  onChange={(e) => set('privacyMode', e.target.checked)}
                />
                <span style={{ fontSize: '0.85rem' }}>Require approval to follow</span>
              </label>
              {form.privacyMode && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>
                    After declining a request, lock that person out for
                  </label>
                  <select
                    value={form.followRequestCooldownAfterReject ?? 'permanent'}
                    onChange={(e) => set('followRequestCooldownAfterReject', e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '0.875rem' }}
                  >
                    <option value="24h">24 hours</option>
                    <option value="7d">7 days</option>
                    <option value="permanent">Permanently (default)</option>
                  </select>
                </div>
              )}
            </Field>
          </Section>

          <div className="ep-form-footer">
            {error && <span className="ep-form-error">{error}</span>}
            <button type="submit" className="btn btn-primary ep-save-btn" disabled={saving}>
              {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save changes'}
            </button>
          </div>
        </form>

        {/* ── Preview ── */}
        <aside className="ep-preview-pane">
          <ProfilePreview form={form} />
        </aside>
      </div>
    </div>
  )
}
